import React, { useEffect, useRef } from "react";
import { Maybe } from "true-myth";

import { SchedulingActionTypes } from "actions/scheduling";
import { useOnRequestSuccess } from "helpers/hooks/useOnRequestStatus";
import {
  HASH_ACTIONS,
  printFrame,
  saveFrame,
} from "helpers/schedule/print_pdf";
import { RequestStatus } from "types/requests";
import { ISchedule } from "types/schedules";

import styled from "styles/styled_components";

import ScheduleTable from "components/schedule/schedule_table";
import StyledLoaderOverlay from "components/shared/styled_loader_overlay";

const StyledErrorMessage = styled.div`
  padding: ${props => props.theme.spacing.small};
  width: 100%;
`;

interface IPublicSchedule {
  assignedSchedule: ISchedule;
  fetchPublishedSchedule: (
    startDate: Date,
    endDate: Date,
    permalink: string,
    forPublic: boolean
  ) => void;
  setScheduleDateBoundaries: (
    startDate: Date,
    endDate: Date
  ) => SchedulingActionTypes;
  permalink: Maybe<string>;
  startDate: Maybe<Date>;
  endDate: Maybe<Date>;
  interactiveView: boolean;
  takeAction: HASH_ACTIONS;
  publishedScheduleRequestStatus: RequestStatus;
}

function PublicSchedule({
  assignedSchedule,
  endDate,
  fetchPublishedSchedule,
  interactiveView,
  permalink,
  publishedScheduleRequestStatus,
  setScheduleDateBoundaries,
  startDate,
  takeAction,
}: IPublicSchedule) {
  // Fetch published schedule
  useEffect(() => {
    permalink.map((link: string) => {
      Maybe.all(startDate, endDate).map(([start, end]) => {
        fetchPublishedSchedule(start, end, link, !interactiveView);
        setScheduleDateBoundaries(start, end);
        return start;
      });

      return link;
    });
  }, []); // 0 dependencies array, run only once

  const tableRef = useRef<HTMLTableElement>(null);

  // Once API fetch completed, take action if specified
  useOnRequestSuccess(() => {
    if (takeAction === HASH_ACTIONS.PRINT) {
      printFrame();
    }
    if (takeAction === HASH_ACTIONS.PDF) {
      if (tableRef.current) {
        saveFrame(tableRef.current, startDate, endDate);
      }
    }
  }, publishedScheduleRequestStatus);

  if (publishedScheduleRequestStatus !== RequestStatus.SUCCESS) {
    return (
      <StyledLoaderOverlay>
        <div>Loading...</div>
      </StyledLoaderOverlay>
    );
  }

  const requiredParams = [permalink, startDate, endDate];

  return Maybe.all(...requiredParams).match({
    Just: ([link, start, end]: Array<string | Date>) => {
      return (
        <ScheduleTable
          dayView={Maybe.nothing()}
          interactiveView={interactiveView}
          schedule={assignedSchedule}
          startDate={start as Date}
          endDate={end as Date}
          tableRef={tableRef}
        />
      );
    },
    Nothing: () => {
      return (
        <StyledErrorMessage>Invalid URL. Please try again.</StyledErrorMessage>
      );
    },
  });
}

export default PublicSchedule;
